import { ConfirmationToken as StripeConfirmationToken } from '@stripe/stripe-js';

import { notify } from 'src/clients/ApiService';
import { AppAPIName, UnAuthAPIName } from 'src/constants';
import {
  ProductStoreLineItem,
  ProductStorePreferencesSchema,
  ProductStoreSchema,
  ProductStoreVisibility,
} from 'src/routes/billing/store/schema';
import {
  Invoice,
  LineItemType,
  updateInvoiceQuery,
} from 'src/services/api/invoicesApi';
import { Price, Product } from 'src/services/api/productsApi';
import { RootState } from 'src/store/reduxTypes';
import { SubscriptionInterval } from 'src/types/subscriptions';
import { API } from 'src/utils/AmplifyApiUtils';
import { IsLoggedIn } from 'src/utils/AuthUtils';
import { ensureApiError } from 'src/utils/Errors';

import { ApiMethods, appAPI } from '.';

export interface ProductStore {
  id: string;
  ref: string;
  displayName: string;
  productStorePreferences: ProductStorePreferencesSchema;
  subscriptions: Array<ProductStoreLineItem>;
  oneTimeProducts: Array<ProductStoreLineItem>;
}

export interface RichProductStoreLineItem extends ProductStoreLineItem {
  product: Product;
  price: Price;
}

export interface RichProductStore extends ProductStore {
  subscriptions: Array<RichProductStoreLineItem>;
  oneTimeProducts: Array<RichProductStoreLineItem>;
}

// TODO: we have a billion of different line item types that we should consolidate
export type LineItem = {
  id: string;
  productId: string;
  priceId: string;
  priceInterval?: SubscriptionInterval;
  quantity: number;
  type: LineItemType;
  amount: number;
  description: string;
};

export type PayProductStoreInput = {
  id: string;
  confirmationToken: StripeConfirmationToken;
  lineItems: Array<LineItem>;
  name?: string;
  email?: string;
  company?: string;
};

export type PayProductStoreResponse = {
  redirectUrl: string;
  invoice: Invoice;
  magicLinkSent: boolean;
};

export interface CreateProductStoreInput extends ProductStoreSchema {
  isPublish: boolean;
  storeURL: string;
}

export interface UpdateProductStoreInput extends ProductStoreSchema {
  id: string;
  isPublish: boolean;
  storeURL: string;
}

export interface GetProductStoreByShortIdInput {
  shortId: string;
}

export interface UpdateProductStoreVisibilityInput {
  id: string;
  visibility: ProductStoreVisibility;
}

const PRODUCT_STORE_VISIBILITY = {
  internal: 'internal',
  clientUser: 'client_user',
  public: 'public',
  all: 'all',
};

const PRODUCT_STORE_STATE_LABELS = {
  savedToWebsite: 'Published to Website',
  savedToPortal: 'Published to Portal',
  savePrivately: 'Saved Privately',
  savedToPortalAndWebsite: 'Published to Portal & Website',
};

const getVisibilitySuccessMessage = (visibility: string): string => {
  switch (visibility) {
    case PRODUCT_STORE_VISIBILITY.internal:
      return PRODUCT_STORE_STATE_LABELS.savePrivately;
    case PRODUCT_STORE_VISIBILITY.clientUser:
      return PRODUCT_STORE_STATE_LABELS.savedToPortal;
    case PRODUCT_STORE_VISIBILITY.public:
      return PRODUCT_STORE_STATE_LABELS.savedToWebsite;
    case PRODUCT_STORE_VISIBILITY.all:
      return PRODUCT_STORE_STATE_LABELS.savedToPortalAndWebsite;
    default:
      return '';
  }
};

export const productStoreApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    getProductStores: build.query<ProductStore[], void>({
      queryFn: async () => {
        const isLoggedInUser = await IsLoggedIn();
        try {
          const response = await API.get(
            isLoggedInUser ? AppAPIName : UnAuthAPIName,
            `${isLoggedInUser ? '' : '/public'}/product_store`,
            {},
          );
          return { data: response };
        } catch (err) {
          const error = ensureApiError(err);
          return { error };
        }
      },
    }),
    getRichProductStoreByShortId: build.query<
      RichProductStore,
      GetProductStoreByShortIdInput
    >({
      queryFn: async ({ shortId }) => {
        const isLoggedInUser = await IsLoggedIn();
        try {
          const response = await API.get(
            isLoggedInUser ? AppAPIName : UnAuthAPIName,
            `${isLoggedInUser ? '' : '/public'}/product_store/${shortId}`,
            {
              queryStringParameters: {
                richResponse: 'true',
              },
            },
          );
          return { data: response };
        } catch (err) {
          const error = ensureApiError(err);
          return { error };
        }
      },
    }),
    createProductStore: build.mutation<ProductStore, CreateProductStoreInput>({
      // We only use isPublish in the onQueryStarted hook
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      query: ({ isPublish, storeURL, ...productStore }) => ({
        path: '/product_store',
        method: ApiMethods.post,
        options: {
          body: {
            ...productStore,
            oneTimeProducts: productStore.oneTimeProducts.map((item) => ({
              ...item,
              description: item.description || '',
            })),
            subscriptions: productStore.subscriptions.map((item) => ({
              ...item,
              description: item.description || '',
            })),
          },
        },
      }),
      async onQueryStarted(
        { isPublish, storeURL },
        { dispatch, queryFulfilled },
      ) {
        try {
          const result = await queryFulfilled;
          const productStore = result.data;
          dispatch(
            productStoreApi.util.updateQueryData(
              'getProductStores',
              undefined,
              (draft) => {
                draft.unshift(productStore);
              },
            ),
          );
          // Use reusable function to get success message
          const visibility = productStore?.productStorePreferences?.visibility;

          if (isPublish) {
            const successMessage = getVisibilitySuccessMessage(visibility);
            const showActionProps =
              visibility === PRODUCT_STORE_VISIBILITY.public ||
              visibility === PRODUCT_STORE_VISIBILITY.all;
            notify({
              status: 'success',
              successMessage,
              dispatch,
              ...(showActionProps && {
                actionProps: {
                  href: storeURL,
                  children: 'View Store',
                  target: '_blank',
                },
              }),
            });
          } else {
            notify({
              status: 'success',
              successMessage: 'Store saved',
              dispatch,
            });
          }
        } catch (err) {
          const error = ensureApiError(err);
          const errorMessage = error.message;
          notify({
            status: 'error',
            errorMessage: errorMessage || 'Could not create product store',
            dispatch,
          });
        }
      },
    }),
    payProductStore: build.mutation<
      PayProductStoreResponse,
      PayProductStoreInput
    >({
      queryFn: async (payProductStoreInput, { dispatch }) => {
        const isLoggedInUser = await IsLoggedIn();
        try {
          const data = await API.post(
            isLoggedInUser ? AppAPIName : UnAuthAPIName,
            `${isLoggedInUser ? '' : '/public'}/product_store/${
              payProductStoreInput.id
            }/pay`,
            {
              body: {
                lineItems: payProductStoreInput.lineItems,
                confirmationToken: payProductStoreInput.confirmationToken,
                ...(payProductStoreInput.name && {
                  name: payProductStoreInput.name,
                }),
                ...(payProductStoreInput.email && {
                  email: payProductStoreInput.email,
                }),
                ...(payProductStoreInput.company && {
                  company: payProductStoreInput.company,
                }),
              },
            },
          );

          return { data };
        } catch (err) {
          const error = ensureApiError(err);
          notify({
            status: 'error',
            errorMessage:
              'Your payment did not succeed. Please ensure your information is correct and try again.',
            dispatch,
          });
          return { error };
        }
      },

      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        const { data } = await queryFulfilled;
        if (data && typeof data === 'object') {
          const state = getState() as unknown as RootState;
          updateInvoiceQuery(data.invoice, dispatch, state);
        }
      },
    }),
    updateProductStore: build.mutation<ProductStore, UpdateProductStoreInput>({
      // We only use isPublish in the onQueryStarted hook
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      query: ({ id, isPublish, storeURL, ...updateProductStore }) => ({
        path: `/product_store/${id}`,
        method: ApiMethods.put,
        options: {
          body: {
            ...updateProductStore,
            oneTimeProducts: updateProductStore.oneTimeProducts.map((item) => ({
              ...item,
              description: item.description || '',
            })),
            subscriptions: updateProductStore.subscriptions.map((item) => ({
              ...item,
              description: item.description || '',
            })),
          },
        },
      }),
      async onQueryStarted(
        { isPublish, storeURL },
        { dispatch, queryFulfilled },
      ) {
        try {
          const result = await queryFulfilled;
          const updatedProductStore = result.data;
          dispatch(
            productStoreApi.util.updateQueryData(
              'getProductStores',
              undefined,
              (draft) => {
                const productStore = draft.find(
                  (store) => store.id === updatedProductStore.id,
                );
                if (productStore) {
                  Object.assign(productStore, updatedProductStore);
                }
              },
            ),
          );

          const visibility =
            updatedProductStore?.productStorePreferences?.visibility;

          if (isPublish) {
            const successMessage = getVisibilitySuccessMessage(visibility);
            const showActionProps =
              visibility === PRODUCT_STORE_VISIBILITY.public ||
              visibility === PRODUCT_STORE_VISIBILITY.all;
            notify({
              status: 'success',
              successMessage,
              dispatch,
              ...(showActionProps && {
                actionProps: {
                  href: storeURL,
                  children: 'View Store',
                  target: '_blank',
                },
              }),
            });
          } else {
            notify({
              status: 'success',
              successMessage: 'Store saved',
              dispatch,
            });
          }
        } catch (err) {
          const error = ensureApiError(err);
          const errorMessage = error.message;
          notify({
            status: 'error',
            errorMessage: errorMessage || 'Could not update product store',
            dispatch,
          });
        }
      },
    }),
    updateProductStoreVisibility: build.mutation<
      ProductStore,
      UpdateProductStoreVisibilityInput
    >({
      query: ({ id, visibility }) => ({
        path: `/product_store/${id}/visibility?visibility=${visibility}`,
        method: ApiMethods.put,
        options: {},
      }),
      async onQueryStarted(props, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            productStoreApi.util.updateQueryData(
              'getProductStores',
              undefined,
              (draft) => {
                const productStoreIndex = draft.findIndex(
                  (store) => store.ref === props.id,
                );
                if (productStoreIndex !== -1) {
                  draft[productStoreIndex].productStorePreferences.visibility =
                    props.visibility;
                }
              },
            ),
          );
          notify({
            status: 'success',
            successMessage: 'Product store visibility updated successfully',
            dispatch,
          });
        } catch (err) {
          const error = ensureApiError(err);
          const errorMessage = error.message;
          notify({
            status: 'error',
            errorMessage:
              errorMessage || 'Could not update product store visibility',
            dispatch,
          });
        }
      },
    }),
  }),
});
export const {
  useGetProductStoresQuery,
  useCreateProductStoreMutation,
  useGetRichProductStoreByShortIdQuery,
  usePayProductStoreMutation,
  useUpdateProductStoreMutation,
  useUpdateProductStoreVisibilityMutation,
} = productStoreApi;
